import { useState, useRef } from 'react';
import { supabase } from '../supabase';
import { Upload, CheckCircle, ChevronLeft, Bike, MessageSquare, User, X } from 'lucide-react';

const NAVY  = '#1e3a6e';
const RED   = '#b83228';
const F     = "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// ── Champ texte ───────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={{ fontSize: '12px', fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
        {label}{required && <span style={{ color: RED }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  padding: '13px 14px', borderRadius: '10px', border: '1.5px solid #dbe4f0',
  fontSize: '14px', color: '#0f172a', background: 'white', outline: 'none',
  fontFamily: F, width: '100%', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const selectStyle = { ...inputStyle, cursor: 'pointer', appearance: 'none' };

// ── Formulaire selon le rôle ──────────────────────────────────────────────────
function FormByRole({ role, values, onChange }) {
  const commun = (
    <>
      <Field label="Prénom et Nom" required>
        <input style={inputStyle} placeholder="Ex : Moussa Diallo" value={values.prenom_nom}
          onChange={e => onChange('prenom_nom', e.target.value)} />
      </Field>

      <Field label="Numéro WhatsApp" required>
        <input style={inputStyle} placeholder="Ex : 77 000 00 00" type="tel" value={values.whatsapp}
          onChange={e => onChange('whatsapp', e.target.value)} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Field label="Âge" required>
          <input style={inputStyle} placeholder="Ex : 24" type="number" min="16" max="60" value={values.age}
            onChange={e => onChange('age', e.target.value)} />
        </Field>
        <Field label="Disponibilités" required>
          <select style={selectStyle} value={values.disponibilites} onChange={e => onChange('disponibilites', e.target.value)}>
            <option value="">Choisir</option>
            <option>Temps plein</option>
            <option>Temps partiel</option>
          </select>
        </Field>
      </div>

      <Field label="Quartier / Zone de résidence" required>
        <input style={inputStyle} placeholder="Ex : Parcelles, Médina, Guédiawaye…" value={values.quartier}
          onChange={e => onChange('quartier', e.target.value)} />
      </Field>
    </>
  );

  if (role === 'livreur') return (
    <>
      {commun}
      <Field label="Moyen de déplacement" required>
        <select style={selectStyle} value={values.moyen_deplacement} onChange={e => onChange('moyen_deplacement', e.target.value)}>
          <option value="">Choisir</option>
          <option>Moto</option>
          <option>Vélo</option>
          <option>À pied</option>
        </select>
      </Field>
      <Field label="Expérience en livraison">
        <select style={selectStyle} value={values.experience_livraison} onChange={e => onChange('experience_livraison', e.target.value)}>
          <option value="">Choisir</option>
          <option>Aucune expérience</option>
          <option>Moins de 6 mois</option>
          <option>6 mois à 1 an</option>
          <option>Plus d'un an</option>
        </select>
      </Field>
      <Field label="Pourquoi veux-tu rejoindre Wakaana Express ?">
        <textarea style={{ ...inputStyle, minHeight: '90px', resize: 'vertical', lineHeight: 1.6 }}
          placeholder="Dis-nous en quelques lignes ce qui te motive…"
          value={values.motivation} onChange={e => onChange('motivation', e.target.value)} />
      </Field>
    </>
  );

  return (
    <>
      {commun}
      <Field label="Expérience en vente">
        <select style={selectStyle} value={values.experience_vente} onChange={e => onChange('experience_vente', e.target.value)}>
          <option value="">Choisir</option>
          <option>Aucune expérience</option>
          <option>Moins de 6 mois</option>
          <option>6 mois à 1 an</option>
          <option>Plus d'un an</option>
        </select>
      </Field>
      <Field label="Tu as déjà vendu via WhatsApp ou réseaux sociaux ?">
        <select style={selectStyle} value={values.vente_whatsapp} onChange={e => onChange('vente_whatsapp', e.target.value)}>
          <option value="">Choisir</option>
          <option>Oui, régulièrement</option>
          <option>Oui, quelques fois</option>
          <option>Non, jamais</option>
        </select>
      </Field>
      <Field label="Convaincs-nous en quelques lignes">
        <textarea style={{ ...inputStyle, minHeight: '90px', resize: 'vertical', lineHeight: 1.6 }}
          placeholder="Parle-nous de toi, de ta motivation, de ce que tu apportes…"
          value={values.motivation} onChange={e => onChange('motivation', e.target.value)} />
      </Field>
    </>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
const EMPTY = {
  prenom_nom: '', whatsapp: '', age: '', quartier: '', disponibilites: '',
  moyen_deplacement: '', experience_livraison: '',
  experience_vente: '', vente_whatsapp: '',
  motivation: '',
};

export default function Recrutement() {
  const [role, setRole]           = useState(null);   // 'livreur' | 'closer'
  const [values, setValues]       = useState(EMPTY);
  const [photo, setPhoto]         = useState(null);   // File
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState('');
  const fileRef = useRef();

  const onChange = (key, val) => setValues(prev => ({ ...prev, [key]: val }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    fileRef.current && (fileRef.current.value = '');
  };

  const required = ['prenom_nom', 'whatsapp', 'age', 'quartier', 'disponibilites'];
  const isValid = () => required.every(k => String(values[k] || '').trim())
    && (role === 'livreur' ? values.moyen_deplacement : true)
    && !!photo;

  const submit = async () => {
    if (!role || !isValid()) {
      if (!photo) setError('La photo de profil est obligatoire.');
      else setError('Remplis tous les champs obligatoires.');
      return;
    }
    setError(''); setSubmitting(true);

    let photo_url = null;
    if (photo) {
      const ext  = photo.name.split('.').pop().toLowerCase();
      const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('recrutement-photos')
        .upload(path, photo, { cacheControl: '3600', upsert: false });
      if (upErr) {
        setSubmitting(false);
        setError(`Échec de l'envoi de la photo : ${upErr.message}. Réessaie.`);
        return;
      }
      const { data } = supabase.storage.from('recrutement-photos').getPublicUrl(path);
      photo_url = data.publicUrl;
    }

    const payload = { role, photo_url, ...values };
    const { error: dbErr } = await supabase.from('job_applications').insert(payload);

    setSubmitting(false);
    if (dbErr) { setError('Une erreur est survenue. Réessaie.'); return; }
    setDone(true);
  };

  // ── Succès ────────────────────────────────────────────────────────────────
  if (done) return (
    <div style={{ minHeight: '100vh', background: '#f0f3f8', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: F }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '40px 32px', textAlign: 'center', maxWidth: '400px', width: '100%', boxShadow: '0 8px 40px rgba(30,58,110,0.12)' }}>
        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#f0f3f8', border: `3px solid ${NAVY}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <CheckCircle size={36} color={NAVY} />
        </div>
        <div style={{ fontSize: '22px', fontWeight: 800, color: NAVY, marginBottom: '10px' }}>
          Candidature envoyée !
        </div>
        <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.7 }}>
          Merci pour ta candidature. Nous allons examiner ton profil et te recontacter très prochainement sur WhatsApp.
        </p>
        <div style={{ marginTop: '24px', fontSize: '12px', color: '#94a3b8' }}>Wakaana Express · Dakar, Sénégal</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f3f8', fontFamily: F }}>

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, #07162d 0%, ${NAVY} 100%)`, padding: '20px 24px 28px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', left: '-30px', bottom: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: `rgba(${parseInt(RED.slice(1,3),16)},${parseInt(RED.slice(3,5),16)},${parseInt(RED.slice(5,7),16)},0.08)` }} />
        {role && (
          <button onClick={() => { setRole(null); setValues(EMPTY); setError(''); }}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', padding: '8px 12px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, marginBottom: '16px' }}>
            <ChevronLeft size={15} /> Retour
          </button>
        )}
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>
          Wakaana Express · Recrutement
        </div>
        <div style={{ fontSize: '22px', fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
          {role === 'livreur' ? 'Candidature Livreur'
            : role === 'closer' ? 'Candidature Closer'
            : 'Rejoins notre équipe'}
        </div>
        {!role && (
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '6px' }}>
            Choisis le poste qui t'intéresse
          </div>
        )}
      </div>

      <div style={{ padding: '20px 16px 40px', maxWidth: '560px', margin: '0 auto' }}>

        {/* ── Étape 1 : choix du rôle ── */}
        {!role && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '4px' }}>
            {[
              {
                key: 'livreur',
                icon: Bike,
                title: 'Livreur',
                desc: 'Tu livres les colis de nos clients à travers Dakar. Terrain, mobilité, contact direct.',
              },
              {
                key: 'closer',
                icon: MessageSquare,
                title: 'Closer',
                desc: 'Tu gères les clients entrants, tu confirmes les commandes et tu crées la relation.',
              },
            ].map(({ key, icon: Icon, title, desc }) => (
              <button key={key} onClick={() => setRole(key)}
                style={{ background: 'white', border: `2px solid #e2e8f0`, borderRadius: '16px', padding: '22px 20px', display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', boxShadow: '0 2px 12px rgba(30,58,110,0.07)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.boxShadow = `0 4px 24px rgba(30,58,110,0.14)`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(30,58,110,0.07)'; }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={24} color="white" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '17px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>{title}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>{desc}</div>
                </div>
                <ChevronLeft size={18} color="#cbd5e1" style={{ transform: 'rotate(180deg)', flexShrink: 0 }} />
              </button>
            ))}
          </div>
        )}

        {/* ── Étape 2 : formulaire ── */}
        {role && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Carte infos */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1.5px solid #eef0f6', boxShadow: '0 2px 12px rgba(30,58,110,0.06)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                <User size={14} color={NAVY} />
                <span style={{ fontSize: '11px', fontWeight: 800, color: NAVY, textTransform: 'uppercase', letterSpacing: '1px' }}>Ton profil</span>
              </div>
              <FormByRole role={role} values={values} onChange={onChange} />
            </div>

            {/* Photo — en dernier */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1.5px solid #eef0f6', boxShadow: '0 2px 12px rgba(30,58,110,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                <Upload size={14} color={NAVY} />
                <span style={{ fontSize: '11px', fontWeight: 800, color: NAVY, textTransform: 'uppercase', letterSpacing: '1px' }}>Photo de profil <span style={{ color: RED }}>*</span></span>
              </div>

              {photoPreview ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <img src={photoPreview} alt="preview" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${NAVY}` }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '6px' }}>Photo sélectionnée</div>
                    <button onClick={removePhoto}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #fecdd3', background: '#fff1f2', color: RED, fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                      <X size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()}
                  style={{ width: '100%', padding: '20px', borderRadius: '12px', border: `2px dashed #c7d4eb`, background: '#f8fafc', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f0f3f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Upload size={20} color={NAVY} />
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: NAVY }}>Ajouter une photo</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>JPG, PNG ou WEBP · max 5 Mo</div>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic"
                style={{ display: 'none' }} onChange={onPhoto} />
            </div>

            {/* Erreur */}
            {error && (
              <div style={{ background: '#fdf2f2', borderLeft: `4px solid ${RED}`, borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: RED, fontWeight: 600 }}>
                {error}
              </div>
            )}

            {/* Bouton soumettre */}
            <button onClick={submit} disabled={submitting}
              style={{ padding: '16px', borderRadius: '14px', border: 'none', background: submitting ? '#94a3b8' : `linear-gradient(135deg, ${RED}, #991b1b)`, color: 'white', fontSize: '15px', fontWeight: 800, cursor: submitting ? 'default' : 'pointer', boxShadow: submitting ? 'none' : `0 6px 20px rgba(184,50,40,0.35)`, letterSpacing: '0.3px' }}>
              {submitting ? 'Envoi en cours...' : 'Envoyer ma candidature'}
            </button>

            <p style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
              Tes informations sont confidentielles et utilisées uniquement pour le recrutement.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
